import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { debug } from '@ember/debug';
import { inject } from '@ember/service';
import { guidFor } from '@ember/object/internals';
import { sanitizeHtmlString } from 'beebee-helper/helpers/safe-string';
import Quill from 'quill';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! .bb-editor is the focus-ring hook: the ring has to wrap toolbar and text box\n    together, so it can\'t live on either of them (see custom_new.scss). The idle\n    stand-in in CfAdvancedTextarea carries the same class for the same reason. }}\n<div class=\'bb-editor\' {{did-update this.onValueChange @value}}>\n<Quill as |Ql|>\n  <Ql.toolbar as |Tb|>\n    <Tb.size @values={{array null \"small\" \"large\" \"huge\"}} />\n    <Tb.group>\n      <Tb.bold />\n      <Tb.italic />\n      <Tb.underline />\n      <Tb.strike />\n    </Tb.group>\n    <Tb.group>\n      <Tb.color />\n      <Tb.background />\n    </Tb.group>\n    <Tb.group>\n      <Tb.align @values={{array null \"center\" \"right\" \"justify\"}} />\n    </Tb.group>\n    <Tb.group>\n      <Tb.list value=\"ordered\" />\n      <Tb.list value=\"bullet\" />\n    </Tb.group>\n    <Tb.group>\n      <Tb.link />\n      <Tb.clean />\n    </Tb.group>\n  </Ql.toolbar>\n\n  <Ql.editor\n    @onInit={{this.onQuillReady}}\n    @onText={{this.adChange}}\n    @name={{this.quillId}}\n    @theme=\"snow\" />\n\n</Quill>\n</div>\n");

var _dec, _class, _descriptor;
const {
  StyleAttributor,
  Scope
} = Quill.import('parchment');
const SIZE_MAP = {
  small: '0.75em',
  normal: '1em',
  large: '1.5em',
  huge: '2.5em'
};
class SizeStyleAttributor extends StyleAttributor {
  add(node, value) {
    const cssValue = SIZE_MAP[value] ?? value;
    return super.add(node, cssValue);
  }
  value(node) {
    const cssValue = super.value(node);
    return Object.keys(SIZE_MAP).find(key => SIZE_MAP[key] === cssValue) ?? cssValue;
  }
}
const SizeStyle = new SizeStyleAttributor('size', 'font-size', {
  scope: Scope.INLINE
});
class AlignStyleAttributor extends StyleAttributor {
  add(node, value) {
    if (value === 'left' || value === false) {
      return super.remove(node);
    }
    return super.add(node, value);
  }
}
const AlignStyle = new AlignStyleAttributor('align', 'text-align', {
  scope: Scope.BLOCK
});
Quill.register(SizeStyle, true);
Quill.register(AlignStyle, true);
let BbEditorComponent = (_dec = inject('quill'), _class = class BbEditorComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "quillService", _descriptor, this);
    this._quill = null;
    this._lastValue = null;
    this._lastArgsValue = null;
    this._isDragging = false;
    this._onDragStart = () => {
      this._isDragging = true;
    };
    this._onDragEnd = () => {
      this._isDragging = false;
      this.adChange();
    };
    this._onDrop = () => {
      this._isDragging = false;
    };
    this._onDragOver = e => {
      e.preventDefault();
    };
  }
  get id() {
    return this.args.id ?? guidFor(this);
  }
  get quillId() {
    return `quill-${this.id}`;
  }
  willDestroy() {
    super.willDestroy(...arguments);
    if (this._quill) {
      this._quill.root.removeEventListener('dragstart', this._onDragStart);
      this._quill.root.removeEventListener('dragend', this._onDragEnd);
      this._quill.root.removeEventListener('drop', this._onDrop);
      this._quill.root.removeEventListener('dragover', this._onDragOver);
    }
    this._quill = null;
    this._lastValue = null;
    this._lastArgsValue = null;
  }

  // Called by @onInit on Ql.editor — quill is fully initialized at this point
  onQuillReady(quill) {
    this._quill = quill;
    this._lastArgsValue = this.args.value;
    this._loadContent(this.args.value);

    // Hand the service a copy of this toolbar, so fields that haven't mounted an editor
    // can still show one. Free here — the toolbar is already built and on screen.
    this.quillService.captureToolbar(quill.getModule('toolbar')?.container);
    if (this.args.activation) {
      this._applyActivation(quill, this.args.activation);
    }
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
      delta.ops = delta.ops.map(op => {
        if (op.attributes?.background) {
          delete op.attributes.background;
        }
        return op;
      });
      return delta;
    });
    quill.root.addEventListener('dragstart', this._onDragStart);
    quill.root.addEventListener('dragend', this._onDragEnd);
    quill.root.addEventListener('drop', this._onDrop);
    quill.root.addEventListener('dragover', this._onDragOver);
  }

  // Called by did-update when @value changes after first render
  onValueChange() {
    if (this._isDragging) return;
    if (!this._quill) return;
    if (this._lastArgsValue === this.args.value) return;
    this._lastArgsValue = this.args.value;
    this._loadContent(this.args.value);
  }

  // @activation is passed by callers that show a copy of the toolbar over a plain text
  // box until the field is used (see CfAdvancedTextarea): it says where the user pressed,
  // if they pressed at all rather than just hovering.
  //
  // The stand-in is the same size as the real thing, so nothing moves when the editor
  // takes over and the recorded point still lands on the character it was aimed at.
  _applyActivation(quill, {
    x,
    y,
    focus
  }) {
    if (!focus) {
      return;
    }
    try {
      const index = x == null || y == null ? null : this._indexFromPoint(quill, x, y);
      quill.setSelection(index ?? quill.getLength() - 1, 0, 'silent');
    } catch (e) {
      debug(`bb-editor: could not place the caret on activation — ${e}`);
      quill.setSelection(quill.getLength() - 1, 0, 'silent');
    }
  }
  _indexFromPoint(quill, x, y) {
    const range = document.caretRangeFromPoint?.(x, y);
    if (!range || !quill.root.contains(range.startContainer)) {
      return null;
    }
    const blot = Quill.find(range.startContainer, true);
    return blot ? blot.offset(quill.scroll) + range.startOffset : null;
  }

  // Quill v2 getSemanticHTML() replaces all spaces with &nbsp; (see slab/quill#4509).
  // This breaks word-wrap in templates outside the editor.
  // The regex keeps consecutive &nbsp; for intentional multiple spaces but converts
  // the trailing one back to a regular space so the browser can break the line.
  //
  // Quill v2 also outputs <p></p> for empty lines. An empty <p> collapses to 0 height
  // when rendered outside the editor, so we add <br> to preserve visual spacing.
  _normalizeHTML(html) {
    if (!html) return html;
    return html.replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ').replaceAll('<p></p>', '<p><br></p>');
  }

  // Quill's clipboard.convert() drops <br> elements that are direct children of the
  // root container (i.e. between block-level elements, not inside a <p>).
  // We restructure flat HTML into explicit <p> blocks so Quill preserves spacing:
  //   - 1 <br>  → paragraph split (no blank line)
  //   - 2 <br>  → paragraph split + 1 empty <p><br></p>  (one blank line)
  //   - N <br>  → paragraph split + (N-1) empty paragraphs
  // The old 1:1 replacement of each <br> with <p><br></p> doubled blank lines
  // because <br /><br /> (one blank line) became two empty paragraphs.
  _preprocessHTMLForQuill(html) {
    if (!html) return html;
    const div = document.createElement('div');
    // Sanitize before assigning to a live element: unlike Quill's own
    // DOMParser-based clipboard.convert, innerHTML on a document-created
    // element executes event handlers (e.g. <img src=x onerror=...>) from
    // stored user content. sanitizeHtmlString strips script tags, on*
    // attributes and javascript: URLs.
    div.innerHTML = sanitizeHtmlString(html);
    const childArray = Array.from(div.childNodes);
    if (!childArray.some(n => n.nodeName === 'BR')) return div.innerHTML;

    // Block-level tags must not be nested inside the synthetic <p>: doing so
    // (e.g. <h1>A</h1><br><p>B</p>) yields <p><h1>…</h1></p> which reparses into
    // extra/broken paragraphs. Flush the inline buffer, then append the block
    // node directly at root level.
    const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'TABLE', 'HR']);
    const result = document.createElement('div');
    let currentP = document.createElement('p');
    const flushP = () => {
      if (currentP.childNodes.length > 0) {
        result.appendChild(currentP);
        currentP = document.createElement('p');
      }
    };
    for (const node of childArray) {
      if (node.nodeName === 'BR') {
        if (currentP.childNodes.length === 0) {
          currentP.innerHTML = '<br>';
        }
        result.appendChild(currentP);
        currentP = document.createElement('p');
      } else if (BLOCK_TAGS.has(node.nodeName)) {
        flushP();
        result.appendChild(node.cloneNode(true));
      } else {
        currentP.appendChild(node.cloneNode(true));
      }
    }
    if (currentP.childNodes.length > 0) {
      result.appendChild(currentP);
    }
    return result.innerHTML;
  }

  // Called on text-change events from the editor (user edits only — programmatic
  // loads are filtered out because _lastValue is synced inside _loadContent)
  adChange() {
    if (this.isDestroying || this.isDestroyed) return;
    if (!this._quill?.root) return;
    const value = this._normalizeHTML(this._quill.getSemanticHTML());
    if (value === this._lastValue) return;
    this._lastValue = value;
    this.args.setValue?.(value);
  }
  _loadContent(html) {
    if (!this._quill) return;
    if (!html) {
      this._quill.setText('');
      // Sync _lastValue so adChange ignores the 'text-change' fired by setText
      this._lastValue = this._normalizeHTML(this._quill.getSemanticHTML());
      return;
    }
    const currentHTML = this._normalizeHTML(this._quill.getSemanticHTML());
    if (currentHTML === html) return;
    const delta = this._quill.clipboard.convert({
      html: this._preprocessHTMLForQuill(html)
    });
    this._quill.setContents(delta, 'silent');
    // Sync _lastValue so adChange ignores the 'text-change' fired by setContents
    this._lastValue = this._normalizeHTML(this._quill.getSemanticHTML());
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "quillService", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onQuillReady", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onQuillReady"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onValueChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onValueChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "adChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "adChange"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbEditorComponent);

export { BbEditorComponent as default };
//# sourceMappingURL=bb-editor.js.map
